import { useState, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Users, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { usePageTitle } from "@/lib/title";
import type { Assignment, Course, Module, Quiz } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, statusTone } from "@/components/ui/Badge";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { CreateLessonModal } from "@/components/courses/CreateLessonModal";
import { CreateQuizModal } from "@/components/quizzes/CreateQuizModal";
import { CreateAssignmentModal } from "@/components/assignments/CreateAssignmentModal";

function ModuleSection({ courseId, module }: { courseId: string; module: Module }) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [addingLesson, setAddingLesson] = useState(false);

  const { mutate: deleteModule } = useMutation({
    mutationFn: () => api.delete(`/api/v1.0/modules/${module.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["courses", courseId, "modules"] });
      notify("Module deleted");
    },
  });

  const { mutate: deleteLesson } = useMutation({
    mutationFn: (lessonId: string) => api.delete(`/api/v1.0/lessons/${lessonId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["courses", courseId, "modules"] });
    },
  });

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h3 className="font-medium text-slate-900">{module.title}</h3>
        <button onClick={() => deleteModule()} className="text-slate-400 hover:text-red-500">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <CardBody className="flex flex-col gap-1">
        {module.lessons.map((lesson) => (
          <div key={lesson.id} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-slate-50">
            <div>
              <span className="text-sm text-slate-800">{lesson.title}</span>
              <span className="ml-2 text-xs capitalize text-slate-400">{lesson.type}</span>
            </div>
            <button onClick={() => deleteLesson(lesson.id)} className="text-slate-400 hover:text-red-500">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button
          onClick={() => setAddingLesson(true)}
          className="mt-2 flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-2 text-sm text-slate-600 hover:bg-slate-50"
        >
          <Plus className="h-4 w-4" /> Add lesson
        </button>
      </CardBody>
      {addingLesson && (
        <CreateLessonModal courseId={courseId} moduleId={module.id} onClose={() => setAddingLesson(false)} />
      )}
    </Card>
  );
}

export function CourseBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const courseId = id!;
  const queryClient = useQueryClient();
  const { notify } = useToast();

  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  const { data: course, isLoading: loadingCourse } = useQuery({
    queryKey: ["courses", courseId],
    queryFn: () => api.get<Course>(`/api/v1.0/courses/${courseId}`),
  });
  const { data: modules, isLoading: loadingModules } = useQuery({
    queryKey: ["courses", courseId, "modules"],
    queryFn: () => api.get<Module[]>(`/api/v1.0/courses/${courseId}/modules`),
  });
  const { data: quizzes } = useQuery({
    queryKey: ["courses", courseId, "quizzes"],
    queryFn: () => api.get<Quiz[]>(`/api/v1.0/courses/${courseId}/quizzes`),
  });
  const { data: assignments } = useQuery({
    queryKey: ["courses", courseId, "assignments"],
    queryFn: () => api.get<Assignment[]>(`/api/v1.0/courses/${courseId}/assignments`),
  });

  const { mutate: createModule, isPending: creatingModule } = useMutation({
    mutationFn: () => api.post(`/api/v1.0/courses/${courseId}/modules`, { title: newModuleTitle }),
    onSuccess: () => {
      setNewModuleTitle("");
      void queryClient.invalidateQueries({ queryKey: ["courses", courseId, "modules"] });
    },
  });

  const { mutate: setStatus } = useMutation({
    mutationFn: (status: string) => api.post(`/api/v1.0/courses/${courseId}/publish`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["courses", courseId] });
      notify("Course status updated");
    },
  });

  const { mutate: deleteQuiz } = useMutation({
    mutationFn: (quizId: string) => api.delete(`/api/v1.0/quizzes/${quizId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["courses", courseId, "quizzes"] }),
  });

  const { mutate: deleteAssignment } = useMutation({
    mutationFn: (assignmentId: string) => api.delete(`/api/v1.0/assignments/${assignmentId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["courses", courseId, "assignments"] }),
  });

  const onCreateModule = (e: FormEvent) => {
    e.preventDefault();
    if (newModuleTitle.trim()) createModule();
  };

  usePageTitle(course?.title ? `Edit: ${course.title}` : "Course Builder");

  if (loadingCourse || loadingModules || !course) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Badge tone={statusTone(course.status)}>{course.status}</Badge>
            <Link to={`/courses/${courseId}`} className="text-sm text-brand-600 hover:underline">
              View as learner
            </Link>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/courses/${courseId}`} state={{ roster: true }}>
            <Button variant="secondary">
              <Users className="h-4 w-4" /> Roster
            </Button>
          </Link>
          {course.status !== "published" && <Button onClick={() => setStatus("published")}>Publish</Button>}
          {course.status === "published" && (
            <Button variant="secondary" onClick={() => setStatus("draft")}>
              Unpublish
            </Button>
          )}
        </div>
      </div>

      <section>
        <h2 className="mb-3 font-semibold text-slate-900">Modules & Lessons</h2>
        <div className="flex flex-col gap-4">
          {modules?.map((m) => (
            <ModuleSection key={m.id} courseId={courseId} module={m} />
          ))}
          <form onSubmit={onCreateModule} className="flex gap-2">
            <input
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="New module title..."
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
            />
            <Button type="submit" loading={creatingModule}>
              <Plus className="h-4 w-4" /> Add module
            </Button>
          </form>
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Quizzes</h2>
          <Button size="sm" variant="secondary" onClick={() => setShowQuizModal(true)}>
            <Plus className="h-4 w-4" /> New quiz
          </Button>
        </div>
        <Card>
          <CardBody className="flex flex-col divide-y divide-slate-100">
            {!quizzes?.length && <p className="py-2 text-sm text-slate-400">No quizzes yet.</p>}
            {quizzes?.map((q) => (
              <div key={q.id} className="flex items-center justify-between py-2.5">
                <span className="text-sm text-slate-800">{q.title}</span>
                <div className="flex items-center gap-3">
                  <Link to={`/quizzes/${q.id}/results`} className="flex items-center gap-1 text-sm text-brand-600 hover:underline">
                    Results <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  <button onClick={() => deleteQuiz(q.id)} className="text-slate-400 hover:text-red-500">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Assignments</h2>
          <Button size="sm" variant="secondary" onClick={() => setShowAssignmentModal(true)}>
            <Plus className="h-4 w-4" /> New assignment
          </Button>
        </div>
        <Card>
          <CardBody className="flex flex-col divide-y divide-slate-100">
            {!assignments?.length && <p className="py-2 text-sm text-slate-400">No assignments yet.</p>}
            {assignments?.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2.5">
                <span className="text-sm text-slate-800">{a.title}</span>
                <div className="flex items-center gap-3">
                  <Link to={`/assignments/${a.id}`} className="flex items-center gap-1 text-sm text-brand-600 hover:underline">
                    Submissions <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  <button onClick={() => deleteAssignment(a.id)} className="text-slate-400 hover:text-red-500">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      </section>

      {showQuizModal && <CreateQuizModal courseId={courseId} onClose={() => setShowQuizModal(false)} />}
      {showAssignmentModal && <CreateAssignmentModal courseId={courseId} onClose={() => setShowAssignmentModal(false)} />}
    </div>
  );
}
